import { components, useApi } from "@appretio/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateCubeMutation } from "./cubeMutationInvalidation";

type DeleteCubesInput = components["schemas"]["DeleteCubesInput"];

/**
 * Mutation hook to delete one or more forecast cubes.
 *
 * A deleted cube can be either a user cube or a budget cube, so both
 * version dropdowns are invalidated — otherwise a deleted budget cube
 * lingered in the budget dropdown until the page reloaded. The
 * invalidation is awaited so ``isPending`` stays true until the cache
 * has caught up (see CLAUDE.md "Mutation cache sync").
 */
export function useDeleteCubes() {
  const apiClient = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeleteCubesInput) => {
      const response = await apiClient.POST("/v2/forecast/cubes/delete", {
        body: input
      });
      if (!response.data?.result) {
        throw new Error("Missing result in delete cubes response");
      }
      return response.data.result;
    },
    onSuccess: async () => {
      await invalidateCubeMutation(queryClient, { versions: true });
    },
    meta: {
      errorMessage: "Failed to delete cubes"
    }
  });
}
